import * as THREE from "three";
import { once } from "./kit";

/** Canvas-drawn textures (decals, signs, normal maps), cached by key. */

export interface TexOptions {
  /** wrap = repeat (tiling textures) */
  repeat?: boolean;
  /** data, not colour (normal maps, masks) */
  linear?: boolean;
  aniso?: number;
}

export function canvasTex(key: string, w: number, h: number, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void, opts: TexOptions = {}): THREE.CanvasTexture {
  return once(`tex:${key}`, () => {
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (ctx) draw(ctx, w, h);
    const t = new THREE.CanvasTexture(canvas);
    t.colorSpace = opts.linear ? THREE.NoColorSpace : THREE.SRGBColorSpace;
    t.anisotropy = opts.aniso ?? 8;
    if (opts.repeat) { t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.RepeatWrapping; }
    return t;
  });
}

/** The display stack every sign uses: heavy, condensed, available everywhere. */
export const DISPLAY_FONT = `"Arial Black", "Archivo Black", Impact, "Helvetica Neue", Arial, sans-serif`;

export function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, size: number, weight = 900): number {
  let s = size;
  ctx.font = `${weight} ${s}px ${DISPLAY_FONT}`;
  while (s > 8 && ctx.measureText(text).width > maxWidth) {
    s -= 2;
    ctx.font = `${weight} ${s}px ${DISPLAY_FONT}`;
  }
  return s;
}

export function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** A soft round glow (white; tint it with the sprite colour). */
export const glowTex = (): THREE.Texture => canvasTex("glow", 128, 128, (ctx) => {
  const g = ctx.createRadialGradient(64, 64, 2, 64, 64, 64);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.22, "rgba(255,255,255,0.55)");
  g.addColorStop(0.55, "rgba(255,255,255,0.14)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
});

/** A contact shadow: dark in the middle, soft edge. */
export const blobTex = (): THREE.Texture => canvasTex("blob", 128, 128, (ctx) => {
  const g = ctx.createRadialGradient(64, 64, 8, 64, 64, 64);
  g.addColorStop(0, "rgba(18,16,40,0.75)");
  g.addColorStop(0.5, "rgba(18,16,40,0.42)");
  g.addColorStop(1, "rgba(18,16,40,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
});

/** Diagonal hazard stripes, `count` stripe pairs across. */
export function stripesTex(a: string, b: string, count: number, w = 512, h = 64, edge?: string): THREE.Texture {
  return canvasTex(`stripes:${a}:${b}:${count}:${w}x${h}:${edge ?? ""}`, w, h, (ctx) => {
    ctx.fillStyle = b;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = a;
    const step = w / count;
    for (let i = -2; i < count + 2; i++) {
      const x = i * step;
      ctx.beginPath();
      ctx.moveTo(x, h); ctx.lineTo(x + step / 2, h); ctx.lineTo(x + step / 2 + h * 0.8, 0); ctx.lineTo(x + h * 0.8, 0);
      ctx.closePath();
      ctx.fill();
    }
    if (edge) {
      ctx.fillStyle = edge;
      ctx.fillRect(0, 0, w, h * 0.07);
      ctx.fillRect(0, h * 0.93, w, h * 0.07);
    }
  });
}

/**
 * A corrugation normal map: trapezoid ribs across u (one period per texture width) — container sides and roofs.
 * Tangent-space: x follows u.
 */
export function corrugationTex(): THREE.Texture {
  return canvasTex("corrugation", 64, 4, (ctx, w, h) => {
    const img = ctx.createImageData(w, h);
    for (let x = 0; x < w; x++) {
      const t = x / w;
      // flat outer (0–0.3), slope down (0.3–0.42), flat inner (0.42–0.78), slope up (0.78–0.9), flat (0.9–1)
      let slope = 0;
      if (t >= 0.3 && t < 0.42) slope = -1.1;                 // dh/du: down into the rib
      else if (t >= 0.78 && t < 0.9) slope = 1.1;
      const len = Math.hypot(slope, 1);
      const nx = -slope / len, nz = 1 / len;
      for (let y = 0; y < h; y++) {
        const i = (y * w + x) * 4;
        img.data[i] = Math.round((nx * 0.5 + 0.5) * 255);
        img.data[i + 1] = 128;
        img.data[i + 2] = Math.round((nz * 0.5 + 0.5) * 255);
        img.data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, { repeat: true, linear: true, aniso: 8 });
}

/** A plate normal map with a seam grid and rivet rows (steel decks, bridge girders). `cells` across the texture. */
export function rivetTex(): THREE.Texture {
  return canvasTex("rivets", 256, 256, (ctx, w, h) => {
    ctx.fillStyle = "rgb(128,128,255)";
    ctx.fillRect(0, 0, w, h);
    // seams: a groove = left wall facing +x, right wall facing −x
    const groove = (horizontal: boolean, at: number) => {
      for (let k = -3; k <= 3; k++) {
        const v = k < 0 ? 90 : k > 0 ? 166 : 128;
        ctx.fillStyle = horizontal ? `rgb(128,${v},255)` : `rgb(${v},128,255)`;
        if (horizontal) ctx.fillRect(0, at + k, w, 1); else ctx.fillRect(at + k, 0, 1, h);
      }
    };
    groove(false, 4); groove(true, 4);
    // rivets: little domes
    for (let y = 18; y < h; y += 28) for (const x of [16, w - 16]) dome(ctx, x, y, 5);
    for (let x = 18; x < w; x += 28) for (const y of [16, h - 16]) dome(ctx, x, y, 5);
  }, { repeat: true, linear: true });
}

function dome(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  for (let y = -r; y <= r; y++) for (let x = -r; x <= r; x++) {
    const d = Math.hypot(x, y) / r;
    if (d > 1) continue;
    const nx = x / r * 0.8, ny = -y / r * 0.8;
    const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
    ctx.fillStyle = `rgb(${Math.round((nx * 0.5 + 0.5) * 255)},${Math.round((ny * 0.5 + 0.5) * 255)},${Math.round((nz * 0.5 + 0.5) * 255)})`;
    ctx.fillRect(cx + x, cy + y, 1, 1);
  }
}

/** A gradient light beam on the road: bright at the near end (v = 0), gone at the far end. */
export const beamTex = (): THREE.Texture => canvasTex("beam", 64, 256, (ctx, w, h) => {
  const g = ctx.createLinearGradient(0, h, 0, 0);
  g.addColorStop(0, "rgba(255,250,220,0.85)");
  g.addColorStop(0.35, "rgba(255,246,210,0.35)");
  g.addColorStop(1, "rgba(255,246,210,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  const side = ctx.createLinearGradient(0, 0, w, 0);
  side.addColorStop(0, "rgba(0,0,0,1)");
  side.addColorStop(0.25, "rgba(0,0,0,0)");
  side.addColorStop(0.75, "rgba(0,0,0,0)");
  side.addColorStop(1, "rgba(0,0,0,1)");
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = side;
  ctx.fillRect(0, 0, w, h);
});

/** Text on a transparent or solid background (signs, liveries). */
export function labelTex(key: string, w: number, h: number, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void): THREE.Texture {
  return canvasTex(`label:${key}`, w, h, draw, { aniso: 8 });
}

/** The ETH diamond as a 2D path (artwork 256 × 417) into a box. */
export function ethPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, height: number): void {
  const s = height / 417;
  const P = (x: number, y: number): [number, number] => [cx + (x - 128) * s, cy + (y - 208.5) * s];
  const poly = (pts: [number, number][]) => { ctx.moveTo(...P(...pts[0])); for (const p of pts.slice(1)) ctx.lineTo(...P(...p)); ctx.closePath(); };
  ctx.beginPath();
  poly([[128, 0], [256, 212.3], [128, 288], [0, 212.3]]);
  poly([[0, 236.6], [128, 312.2], [256, 236.6], [128, 417]]);
}

/** The Solana mark as a 2D path (viewBox 101 × 88, y down) into a box `width` wide. */
export function solPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, width: number): void {
  const s = width / 101;
  const P = (x: number, y: number): [number, number] => [cx + (x - 50.5) * s, cy + (y - 44) * s];
  const bars: [number, number][][] = [
    [[20, 0], [101, 0], [81, 21.8], [0, 21.8]],
    [[0, 33.1], [81, 33.1], [101, 54.9], [20, 54.9]],
    [[20, 66.2], [101, 66.2], [81, 88], [0, 88]]
  ];
  ctx.beginPath();
  for (const bar of bars) { ctx.moveTo(...P(...bar[0])); for (const p of bar.slice(1)) ctx.lineTo(...P(...p)); ctx.closePath(); }
}
